import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import {
  IMPACTS_URL,
  INCIDENT_PRIORITY_URL,
  PRIORITIES_URL,
  URGENCIES_URL,
} from "../../Constants/Constants";
import AxiosInstance from "../../services/AxiosInstance";

// Styled components for better visual hierarchy
const MatrixCell = styled(Box)(({ theme, isHeader, isCorner }) => ({
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: isHeader
    ? theme.palette.primary.main
    : isCorner
      ? theme.palette.grey[100]
      : theme.palette.background.paper,
  color: isHeader
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  textAlign: "center",
  minHeight: "80px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: isHeader ? 600 : 400,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: isHeader
      ? theme.palette.primary.dark
      : theme.palette.action.hover,
    transform: isHeader ? "none" : "scale(1.02)",
  },
}));

const PrioritySelect = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
  "& .MuiSelect-select": {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
        },
      }));

const MatrixContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(0),
  margin: theme.spacing(0),
  boxShadow: theme.shadows[2],
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
}));

function IncidentPriorityMatrix() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDarkMode = theme.palette.mode === "dark";
  // State management
    const [priorities, setPriorities] = useState([]);
    const [urgencies, setUrgencies] = useState([]);
    const [impacts, setImpacts] = useState([]);
  const [incidentPriorities, setIncidentPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Matrix state - using a more organized structure
  const [matrixValues, setMatrixValues] = useState({
    "1-1": "", // Immediate-High
    "1-2": "", // Immediate-Medium
    "1-3": "", // Immediate-Low
    "2-1": "", // High-High
    "2-2": "", // High-Medium
    "2-3": "", // High-Low
    "3-1": "", // Medium-High
    "3-2": "", // Medium-Medium
    "3-3": "", // Medium-Low
    "4-1": "", // Low-High
    "4-2": "", // Low-Medium
    "4-3": "", // Low-Low
      });

    useEffect(() => {
    initializeData();
    }, []);

  const initializeData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        getPriorities(),
        getUrgencies(),
        getImpacts(),
        getIncidentPriorities(),
      ]);
    } catch (error) {
      showSnackbar("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  };
    
    const getPriorities = async () => {
    const response = await AxiosInstance.get(PRIORITIES_URL);
    setPriorities(response.data.data);
  };

    const getUrgencies = async () => {
    const response = await AxiosInstance.get(URGENCIES_URL);
    setUrgencies(response.data);
  };

    const getImpacts = async () => {
    const response = await AxiosInstance.get(IMPACTS_URL);
    setImpacts(response.data);
  };

    const getIncidentPriorities = async () => {
    const response = await AxiosInstance.get(INCIDENT_PRIORITY_URL);
            setIncidentPriorities(response.data);

    // Map existing values to matrix
    const newMatrixValues = { ...matrixValues };
    response.data.forEach((incpr) => {
      const key = `${incpr.urgency.seq}-${incpr.impact.seq}`;
      if (newMatrixValues.hasOwnProperty(key)) {
        newMatrixValues[key] = incpr.priority.id;
      }
    });
    setMatrixValues(newMatrixValues);
  };

  const handleMatrixChange = (urgencySeq, impactSeq, priorityId) => {
    const key = `${urgencySeq}-${impactSeq}`;
    setMatrixValues((prev) => ({
      ...prev,
      [key]: priorityId,
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const incidentPriorityList = [];

      Object.entries(matrixValues).forEach(([key, priorityId]) => {
        if (priorityId && priorityId !== "none") {
          const [urgencySeq, impactSeq] = key.split("-").map(Number);
          const priority = priorities.find((p) => p.id === priorityId);
          const urgency = urgencies.find((u) => u.seq === urgencySeq);
          const impact = impacts.find((i) => i.seq === impactSeq);
          if (priority && urgency && impact) {
            incidentPriorityList.push({ priority, urgency, impact });
          }
        }
      });

      // Save each priority
      for (const incidentPriority of incidentPriorityList) {
        await AxiosInstance.post(INCIDENT_PRIORITY_URL, incidentPriority);
      }

      showSnackbar("Priority matrix saved successfully!");
    } catch (error) {
      showSnackbar("Error saving priority matrix", "error");
    } finally {
      setSaving(false);
    }
  };

  const getUrgencyLabel = (seq) => {
    const urgency = urgencies.find((u) => u.seq === seq);
    return urgency ? urgency.name : `Urgency ${seq}`;
  };

  const getImpactLabel = (seq) => {
    const impact = impacts.find((i) => i.seq === seq);
    return impact ? impact.name : `Impact ${seq}`;
  };

  const getPriorityColor = (priorityId) => {
    if (!priorityId || priorityId === "none") return "default";
    const priority = priorities.find((p) => p.id === priorityId);
    //  console.log("priorityId", priorityId, priority);
    if (!priority) return "default";

    // Map priority names to colors
    const priorityColors = {
      P1: "error",
      P2: "warning",
      P3: "info",
      None: "success",
    };
    return priorityColors[priority.name] || "default";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Matrix Container */}
      <MatrixContainer elevation={3}>
        <Grid container spacing={0}>
          {/* Corner cell */}
          <Grid item xs={12} sm={2}>
            <MatrixCell
              isCorner
              sx={{
                backgroundColor: theme.palette.grey[500],
                color: theme.palette.common.white,
                "&:hover": {
                  backgroundColor: theme.palette.grey[600],
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Urgency → Impact ↓
              </Typography>
            </MatrixCell>
          </Grid>

          {/* Urgency headers */}
          {urgencies.map((urgency) => (
            <Grid item xs={12} sm={2.5} key={urgency.seq}>
              <MatrixCell isHeader>
                <Typography variant="subtitle1" fontWeight="bold">
                  {urgency.name}
                </Typography>
              </MatrixCell>
            </Grid>
          ))}

          {/* Matrix rows */}
          {impacts.map((impact) => (
            <React.Fragment key={impact.seq}>
              {/* Impact label */}
              <Grid item xs={12} sm={2}>
                <MatrixCell isHeader>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {impact.name}
                    </Typography>
                  </Box>
                </MatrixCell>
              </Grid>

              {/* Priority selects for this impact */}
              {urgencies.map((urgency) => {
                const key = `${urgency.seq}-${impact.seq}`;
                const value = matrixValues[key] || "";

                return (
                  <Grid
                    item
                    xs={12}
                    sm={2.5}
                    key={`${urgency.seq}-${impact.seq}`}
                  >
                    <MatrixCell>
                      <PrioritySelect size="small" fullWidth>
                        <Select
                          value={value}
                          onChange={(e) =>
                            handleMatrixChange(
                              urgency.seq,
                              impact.seq,
                              e.target.value
                            )
                          }
                          displayEmpty
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Priority</em>
                          </MenuItem>
                          {priorities.map((priority) => (
                            <MenuItem key={priority.id} value={priority.id}>
                              <Chip
                                label={priority.name}
                                size="small"
                                variant="outlined"
                                color={getPriorityColor(priority.id)}
                                sx={{ mr: 1 }}
                              />
                              {priority.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </PrioritySelect>
                    </MatrixCell>
                  </Grid>
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
      </MatrixContainer>

      {/* Legend */}
      <Card sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Priority Legend
          </Typography>
          <Grid container spacing={2}>
            {priorities.map((priority) => (
              <Grid item key={priority.id}>
                <Chip
                  label={priority.name}
                  color={getPriorityColor(priority.id)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? "Saving..." : "Save Configuration"}
                </Button>
            </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </Box>
    );
}

export default IncidentPriorityMatrix;
